<template>
  <v-app class="main">
    <!-- Loading screen while frontend config is being loaded -->
    <div v-if="loadingConfig" class="config-loader">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
      <p class="mt-4">{{ $t('root.loadingConfiguration') }}</p>
    </div>
    
    <!-- Show paywall if subscription is expired -->
    <paywall
      v-if="showPaywall && !suspensionActive && !loadingConfig"
      :subscription-data="subscriptionData"
      @pay-now="handlePayNow"
    ></paywall>
    
    <!-- Suspended account: blocks every page except Bills & payments -->
    <suspended-overlay
      v-if="suspensionActive && !onBillsPage && !loadingConfig"
      :suspension="suspension"
      @logout="logoutSuspended"
    ></suspended-overlay>
    <!-- Show main app (paywall will overlay if expired) -->
    <template v-if="!loadingConfig">
      <!-- keyed on locale: a language change remounts the tree so that labels
           captured in data() (Vuetify header arrays, mostly) are rebuilt; and on
           the plan entitlements, so the menu follows a plan change -->
      <app-main-navbar v-if="display && !showPaywall && !suspensionActive" :key="navKey"></app-main-navbar>
      <!-- Suspended account on Bills & payments, the one page it can still open -->
      <div v-if="suspensionActive && onBillsPage" class="subscription-grace-banner">
        <v-icon small color="white" class="me-2">mdi-lock-alert-outline</v-icon>
        <span class="grace-text">{{ $t('saasPlan.suspendedMessage') }}</span>
        <v-btn small depressed class="grace-pay-btn ms-3" @click="logoutSuspended">
          {{ $t('core.logout') }}
        </v-btn>
      </div>
      <!-- Grace-period banner: bill generated, 3-day window to pay before paywall -->
      <div v-if="graceBanner && !showPaywall" class="subscription-grace-banner">
        <v-icon small color="white" class="mr-2">mdi-clock-alert-outline</v-icon>
        <span class="grace-text">{{ graceBanner.label }}</span>
        <v-btn small depressed class="grace-pay-btn ml-3" @click="handlePayNow">
          {{ $t('common.payNow') }}
        </v-btn>
        <v-btn icon x-small class="ribbon-close-btn ml-2" :aria-label="$t('root.dismiss')" @click="dismissGraceBanner">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
      <!-- Trial-ending banner (no payment needed yet) -->
      <div v-if="trialWarning && !showPaywall" class="subscription-expiry-ribbon">
        <v-icon small color="white" class="mr-2">mdi-alert-circle-outline</v-icon>
        <span>{{ trialWarning.label }}</span>
        <v-btn icon x-small class="ribbon-close-btn ml-2" :aria-label="$t('root.dismiss')" @click="dismissExpiryRibbon">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
      <v-main>
        <!-- SaaS notices from the status poll: maintenance + announcements -->
        <div v-if="display && !showPaywall && !suspensionActive && (maintenanceMessage || visibleAnnouncements.length)" class="saas-notices px-4 px-sm-8 pt-4">
          <maintenance-banner v-if="maintenanceMessage" :message="maintenanceMessage" @dismiss="dismissMaintenance"></maintenance-banner>
          <announcements-stack :announcements="visibleAnnouncements" @dismiss="dismissAnnouncementItem"></announcements-stack>
        </div>
        <router-view v-if="suspensionActive ? onBillsPage : !showPaywall" :key="'view-' + $i18n.locale"></router-view>
        <v-snackbar v-model="snackbar" timeout="3000" right top :color="color">{{ text }}</v-snackbar>
      </v-main>
      <!-- answers plan-gate refusals (FEATURE_NOT_IN_PLAN / PLAN_LIMIT_REACHED); ACCOUNT_SUSPENDED
           brings up the suspended overlay instead -->
      <plan-upgrade-dialog overlay-handles-suspension></plan-upgrade-dialog>
      <!-- "What's new" tour: held back while the paywall or the login page is up -->
      <feature-tour-dialog
        v-model="featureTourOpen"
        :tours="featureTours"
        @close="onFeatureTourClose"
      ></feature-tour-dialog>
      <!-- one-time "new Reports section" notice, shown once per browser -->
      <whats-new-dialog v-if="display && !showPaywall && !suspensionActive"></whats-new-dialog>
    </template>
  </v-app>
</template>

<script>
import MainNavbar from "./core/components/MainNavbar.vue";
import Paywall from "./core/components/Paywall.vue";
import SuspendedOverlay from "./core/components/SuspendedOverlay.vue";
import PlanUpgradeDialog from "./core/components/PlanUpgradeDialog.vue";
import WhatsNewDialog from "./core/components/WhatsNewDialog.vue";
import MaintenanceBanner from "./core/components/MaintenanceBanner.vue";
import AnnouncementsStack from "./core/components/AnnouncementsStack.vue";
import { checkSubscriptionStatus, isSubscriptionExpired, initiatePayment, dismissAnnouncement } from "./utils/subscription-service";
import { loadAndApplyBrandSettings, applyCachedBrandSettings } from "./utils/brand-settings-loader";
import { loadAppConfigurations } from "./utils/config-loader";
import FeatureTourDialog from "./core/components/FeatureTourDialog.vue";
import { loadPendingTours, markToursSeen, toursForCurrentUser } from "./feature-tours/feature-tour-service";
import { applyEntitlements, entitlementsState, isPlanSuspended, lockedFeatureOf } from "./utils/entitlements";
import { bindPlanErrorBus, PLAN_ERROR_EVENT } from "./utils/plan-errors";
import { performLogout } from "./utils/auth-interceptor";

export default {
  components: {
    "app-main-navbar": MainNavbar,
    paywall: Paywall,
    "feature-tour-dialog": FeatureTourDialog,
    "suspended-overlay": SuspendedOverlay,
    "plan-upgrade-dialog": PlanUpgradeDialog,
    "whats-new-dialog": WhatsNewDialog,
    "maintenance-banner": MaintenanceBanner,
    "announcements-stack": AnnouncementsStack,
  },
  data() { return ({
    snackbar: false,
    text: "",
    color: "",
    subscriptionData: null,
    showPaywall: false,
    subscriptionCheckInterval: null,
    loadingConfig: true,
    dismissedExpiryKey: null,
    dismissedGraceKey: null,
    featureTours: [],
    showFeatureTour: false,
    // an ACCOUNT_SUSPENDED refusal arrived: block now, the next status poll gives the details
    apiSuspended: false,
    lastSuspensionCheck: 0,
    dismissedAnnouncementIds: [],
    dismissedMaintenanceKey: null,
  }); },
  computed: {
    featureTourOpen: {
      get() {
        return this.showFeatureTour && this.featureTours.length > 0 && this.display && !this.showPaywall
          && this.$store.state.featureToursEnabled;
      },
      set(open) {
        this.showFeatureTour = open;
      },
    },
    navKey() {
      return "nav-" + this.$i18n.locale + "-" + entitlementsState.rev + "-" + entitlementsState.version;
    },
    // { since, reason } while the SaaS account is suspended (DESIGN §6.1), else null
    suspension() {
      const fromStatus = this.subscriptionData && this.subscriptionData.suspended;
      if (fromStatus && typeof fromStatus === "object") return fromStatus;
      if (fromStatus === true || isPlanSuspended() || this.apiSuspended) return {};
      return null;
    },
    // only on the signed-in pages (the ones that get the navbar): never on sign-in or the public
    // invoice-download / account-deletion pages
    suspensionActive() {
      return !!this.suspension && this.display;
    },
    onBillsPage() {
      return this.$route.path === "/bills-and-payments";
    },
    maintenanceMessage() {
      const maintenance = this.subscriptionData && this.subscriptionData.maintenance;
      if (!maintenance) return null;
      const message = maintenance.message || {};
      const key = JSON.stringify(message);
      return this.dismissedMaintenanceKey === key ? null : message;
    },
    visibleAnnouncements() {
      const list = (this.subscriptionData && this.subscriptionData.announcements) || [];
      return Array.isArray(list) ? list.filter(item => item && item.id && this.dismissedAnnouncementIds.indexOf(item.id) === -1) : [];
    },
    display() {
      if (!this.$route || !this.$route.path) return false;
      const currentPath = this.$route.path;
      // Exact match: "/privacy-policy-settings" (admin screen) must still get the navbar.
      const isPublicPolicyPage = currentPath.replace(/\/+$/, "") === "/privacy-policy";
      if (currentPath === "/" || currentPath.includes("invoice-download") || currentPath.includes("/delete") && !isPublicPolicyPage) {
        return false;
      }
      if (this.$route.meta && this.$route.meta.display === false) {
        return false;
      }
      return true;
    },
    isAuthenticated() {
      return !!localStorage.getItem("apollo-done-token");
    },
    graceBanner() {
      // Shown when a bill has been generated and the 3-day grace window is
      // still open. Dismissible per-session: close hides it until the page
      // reloads (or the bill is paid, which flips in_grace_period off).
      const data = this.subscriptionData;
      if (!data || !data.in_grace_period) return null;
      // The grace window has two sources and only one of them carries a bill:
      // an unpaid bill (invoice id present) or a subscription past its end_date
      // with every bill paid (invoice id null). Key the dismissal on the window
      // itself so the close button works in both cases; grace_period_ends_at is
      // stable across the status poll, so a dismissed banner stays dismissed.
      const invoiceId = data.pending_invoice_id;
      const dismissKey = invoiceId || data.grace_period_ends_at || data.bill_generated_at || "expiry-grace";
      if (this.dismissedGraceKey === dismissKey) return null;
      const days = data.grace_days_remaining;
      if (typeof days !== "number") return null;
      const dayWord = days === 1 ? this.$t('root.day') : this.$t('root.days');
      // Yearly-cycle tenants get one consolidated bill per cycle — say so
      // instead of "monthly" (billing_cycle comes from the SaaS subscription).
      const cycleWord = (data.subscription || {}).billing_cycle === "yearly" ? this.$t("billing.yearly") : this.$t("billing.monthly");
      const label = days > 0
        ? this.$t("billing.billDueWithin", { cycle: cycleWord, days: days, dayWord: dayWord })
        : this.$t("billing.billDueToday", { cycle: cycleWord });
      return { days, label, invoiceId, dismissKey };
    },
    trialWarning() {
      // Pre-billing reminder shown only during a free-trial window. The
      // non-trial "5 days before end of month" reminder has been replaced by
      // the bill-driven graceBanner.
      const data = this.subscriptionData;
      if (!data || !data.has_subscription) return null;
      const sub = data.subscription || {};
      const isTrial = sub.status === "trial" || sub.is_trial;
      if (!isTrial) return null;
      const days = data.days_remaining;
      if (typeof days !== "number" || days <= 0 || days > 5) return null;
      const endDate = sub.end_date;
      if (endDate && this.dismissedExpiryKey === endDate) return null;
      const dayWord = days === 1 ? this.$t('root.day') : this.$t('root.days');
      const label = this.$t("billing.trialEndsIn", { days: days, dayWord: dayWord });
      return { days, label, endDate };
    },
  },
  async created() {
    // Brand settings: apply the cached copy instantly instead of blocking the
    // whole app behind a network round trip on every load; refresh from the
    // server in the background. Only a first-ever visit (no cache yet) waits,
    // and never longer than 4s.
    const hadCache = applyCachedBrandSettings();
    if (hadCache) {
      this.loadingConfig = false;
      loadAndApplyBrandSettings().catch(error => {
        console.warn('Failed to refresh brand settings:', error);
      });
    } else {
      try {
        await Promise.race([
          loadAndApplyBrandSettings(),
          new Promise(resolve => setTimeout(resolve, 4000)),
        ]);
      } catch (error) {
        console.warn('Failed to load brand settings:', error);
      }
      this.loadingConfig = false;
    }

    // Load app configurations (for currency symbol, etc.)
    // This runs in parallel and doesn't block the UI
    if (this.isAuthenticated) {
      loadAppConfigurations().catch(error => {
        console.debug('Failed to load app configurations on startup:', error.message);
      });
    }
  },
  mounted() {
    this.$root.$on("show-alert", (type, text) => {
      this.text = text;
      this.color = type;
      this.snackbar = true;
    });
    
    // Plan-gate refusals from any GraphQL/REST call (utils/plan-errors.js)
    bindPlanErrorBus(this.$root);
    this.$root.$on(PLAN_ERROR_EVENT, this.onPlanError);

    // Listen for login event
    this.$root.$on("user-logged-in", (data) => {
      // If subscription data is provided in the event, use it immediately
      if (data && data.subscriptionData) {
        this.subscriptionData = data.subscriptionData;
        this.showPaywall = data.expired || false;
        this.applyPlanEntitlements(data.subscriptionData);
      } else {
        // Otherwise, check subscription after a short delay
        setTimeout(() => {
          this.checkSubscription();
        }, 200);
      }
      
      // Fetch fresh app configurations (currency symbol, timezone, etc.) on login
      loadAppConfigurations().catch(error => {
        console.debug('Failed to load app configurations on user-logged-in:', error.message);
      });

      this.checkFeatureTours();
      
      // Set up polling if not already set
      if (!this.subscriptionCheckInterval) {
        this.subscriptionCheckInterval = setInterval(() => {
          this.checkSubscription();
        }, 30 * 60 * 1000);
      }
    });
    
    this.$root.$on("open-feature-tours", () => this.openAllFeatureTours());

    // Check subscription on app load if user is authenticated
    if (this.isAuthenticated) {
      this.checkSubscription();
      this.checkFeatureTours();

      // Set up polling every 30 minutes (30 * 60 * 1000 ms)
      this.subscriptionCheckInterval = setInterval(() => {
        this.checkSubscription();
      }, 30 * 60 * 1000);
    }
  },
  beforeDestroy() {
    // Clear interval when component is destroyed
    if (this.subscriptionCheckInterval) {
      clearInterval(this.subscriptionCheckInterval);
      this.subscriptionCheckInterval = null;
    }
    // Remove event listeners
    this.$root.$off("user-logged-in");
    this.$root.$off("open-feature-tours");
    this.$root.$off(PLAN_ERROR_EVENT, this.onPlanError);
    bindPlanErrorBus(null);
  },
  watch: {
    // Watch for authentication changes (login/logout)
    isAuthenticated(newVal, oldVal) {
      if (newVal && !oldVal) {
        // User just logged in - check subscription after a delay to ensure token is set
        setTimeout(() => {
          this.checkSubscription();
        }, 400);
        // Set up polling
        if (!this.subscriptionCheckInterval) {
          this.subscriptionCheckInterval = setInterval(() => {
            this.checkSubscription();
          }, 30 * 60 * 1000);
        }
      } else if (!newVal) {
        // User logged out - clear subscription data and stop polling
        this.subscriptionData = null;
        this.showPaywall = false;
        if (this.subscriptionCheckInterval) {
          clearInterval(this.subscriptionCheckInterval);
          this.subscriptionCheckInterval = null;
        }
      }
    },
  },
  methods: {
    // Show tours for features released since this user last looked
    async checkFeatureTours() {
      // read the token directly: the isAuthenticated computed is cached from
      // the login page (localStorage isn't reactive) and stays false after login
      if (!localStorage.getItem("apollo-done-token")) return;
      const { enabled, tours } = await loadPendingTours();
      this.$store.commit("setFeatureToursEnabled", enabled);
      if (tours.length) {
        this.featureTours = tours;
        this.showFeatureTour = true;
      }
    },
    // "What's new" in the navbar: replay every tour this user can see
    openAllFeatureTours() {
      if (!this.$store.state.featureToursEnabled) return;
      const all = toursForCurrentUser();
      if (!all.length) {
        this.$root.$emit("show-alert", "info", this.$t("featureTours.nothingNew"));
        return;
      }
      this.featureTours = all;
      this.showFeatureTour = true;
    },
    onFeatureTourClose(ids) {
      markToursSeen(ids);
    },
    async checkSubscription() {
      // Only check if user is authenticated
      if (!this.isAuthenticated) {
        return;
      }
      
      try {
        const result = await checkSubscriptionStatus();
        
        if (result.success && result.data) {
          this.subscriptionData = result.data;
          this.showPaywall = isSubscriptionExpired(result.data);
          this.applyPlanEntitlements(result.data);
        } else {
          // If API call fails, we'll assume subscription is valid to avoid blocking users
          // You can change this behavior based on your requirements
          this.showPaywall = false;
        }
      } catch (error) {
        // On error, assume subscription is valid to avoid blocking users
        this.showPaywall = false;
      }
    },
    dismissExpiryRibbon() {
      const endDate = this.trialWarning && this.trialWarning.endDate;
      if (!endDate) return;
      this.dismissedExpiryKey = endDate;
    },
    dismissGraceBanner() {
      const key = this.graceBanner && this.graceBanner.dismissKey;
      if (!key) return;
      this.dismissedGraceKey = key;
    },
    // Puts the plan entitlements of a status response in force; when they changed and the open
    // page needs a feature the plan no longer has, move to My plan (the router guard only runs
    // on navigation).
    applyPlanEntitlements(statusData) {
      // a fresh status is the source of truth for suspension: when it says "not suspended", drop
      // what an earlier ACCOUNT_SUSPENDED refusal set, so a reactivation lifts the overlay at once
      if (statusData && !statusData.suspended) {
        this.apiSuspended = false;
        this.lastSuspensionCheck = 0;
      }
      if (!applyEntitlements(statusData)) return;
      const locked = lockedFeatureOf(this.$route);
      if (locked) {
        this.$router.replace({ name: "MyPlan", query: { locked } }).catch(() => {});
      }
    },
    onPlanError(detail) {
      if (!detail || detail.type !== "ACCOUNT_SUSPENDED") return;
      this.apiSuspended = true;
      // refresh the status (reason, since, bills) at most once a minute
      const now = Date.now();
      if (now - this.lastSuspensionCheck > 60 * 1000) {
        this.lastSuspensionCheck = now;
        this.checkSubscription();
      }
    },
    logoutSuspended() {
      performLogout();
    },
    dismissMaintenance() {
      this.dismissedMaintenanceKey = JSON.stringify((this.subscriptionData && this.subscriptionData.maintenance && this.subscriptionData.maintenance.message) || {});
    },
    dismissAnnouncementItem(id) {
      // hide at once; the SaaS backend stops returning it once the dismissal is stored
      if (this.dismissedAnnouncementIds.indexOf(id) === -1) this.dismissedAnnouncementIds.push(id);
      dismissAnnouncement(id);
    },
    async handlePayNow() {
      try {
        if (!this.subscriptionData || !this.subscriptionData.subscription) {
          this.$root.$emit("show-alert", "error", "Subscription information not available");
          return;
        }

        const subscription = this.subscriptionData.subscription;
        const subscriptionId = subscription.id;
        const plan = subscription.plan;
        
        if (!subscriptionId || !plan) {
          this.$root.$emit("show-alert", "error", "Missing subscription or plan information");
          return;
        }

        // Get client ID from subscription data or extract from tenant context
        // For now, we'll need to get it from the subscription status response
        // The SASS backend should include client_id in the subscription status response
        const clientId = this.subscriptionData.client_id || subscription.client_id;
        
        if (!clientId) {
          this.$root.$emit("show-alert", "error", "Client information not available");
          return;
        }

        // Amount will be calculated by backend based on pricing structure and usage
        // Pass 0 or omit amount to let backend calculate it
        const amount = 0;

        this.$root.$emit("show-alert", "info", "Initiating payment...");

        const result = await initiatePayment(subscriptionId, clientId, amount);

        if (result.success && result.data && result.data.redirect_url) {
          // Redirect to payment gateway
          window.location.href = result.data.redirect_url;
        } else {
          const errorMsg = result.error || this.$t('root.failedToInitiatePayment');
          this.$root.$emit("show-alert", "error", errorMsg);
        }
      } catch (error) {
        console.error("Payment initiation error:", error);
        this.$root.$emit("show-alert", "error", error.message || this.$t('root.failedToInitiatePayment'));
      }
    },
  },
};
</script>

<style>
.main {
  background-color: #F8FBFB !important;
}

/* Selected option in dropdown menus: Vuetify tints the active item with the
   brand primary color, which some tenant palettes render unreadable (near-white
   on white). Force readable text and keep the highlight as a subtle overlay. */
.v-menu__content .v-select-list .v-list-item--active,
.v-menu__content .v-select-list .v-list-item--active .v-list-item__title,
.v-menu__content .v-select-list .v-list-item--active .v-list-item__content {
  color: rgba(0, 0, 0, 0.87) !important;
}
.v-menu__content .v-select-list .v-list-item--active::before {
  opacity: 0.08;
}

@font-face {
  font-family: "Gotham";
  src: local("Gotham"), url(./fonts/Gotham/GothamBook.ttf) format("swap");
}

.config-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  z-index: 9999;
}

.config-loader p {
  color: #666;
  font-size: 16px;
}

.subscription-expiry-ribbon {
  position: sticky;
  top: 0;
  z-index: 5;
  background-color: #f59e0b;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.subscription-expiry-ribbon .ribbon-close-btn {
  background: transparent !important;
}

.subscription-expiry-ribbon .ribbon-close-btn:hover {
  opacity: 0.8;
}

.subscription-grace-banner {
  position: sticky;
  top: 0;
  z-index: 6;
  background-color: #dc2626;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.16);
}

.subscription-grace-banner .grace-text {
  letter-spacing: 0.1px;
}

.subscription-grace-banner .grace-pay-btn {
  background-color: #fff !important;
  color: #dc2626 !important;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
}

.subscription-grace-banner .grace-pay-btn:hover {
  background-color: #fef2f2 !important;
}
</style>